import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, NavLink, Link } from 'react-router-dom';
import io from 'socket.io-client';
import { jwtDecode } from 'jwt-decode';
import './App.css';
import Series from './components/Series';
import Teams from './components/Teams';
import Players from './components/Players';
import Users from './components/Users';
import Tournaments from './components/Tournaments';
import MatchPage from './components/MatchPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [socket, setSocket] = useState(null);
  const [user, setUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [series, setSeries] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [matches, setMatches] = useState([]);
  const isLoggedIn = Boolean(token);

  const navItems = [
    { key: 'series', path: '/series', label: 'Sarjat', icon: '📅' },
    { key: 'tournaments', path: '/tournaments', label: 'Turnaukset', icon: '🏆' },
    { key: 'matches', path: '/matches', label: 'Ottelut', icon: '🏒' },
    { key: 'teams', path: '/teams', label: 'Joukkueet', icon: '👥' },
    { key: 'players', path: '/players', label: 'Pelaajat', icon: '👤' },
    { key: 'users', path: '/users', label: 'Käyttäjät', icon: '🛡️' },
  ];
  const publicNavKeys = new Set(['tournaments', 'matches']);
  const visibleNavItems = navItems.filter((item) => isLoggedIn || publicNavKeys.has(item.key));

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUser(decodedToken);
      } catch (_error) {
        setUser(null);
        localStorage.removeItem('token');
        setToken(null);
        return;
      }
    } else {
      setUser(null);
    }

    const connectionOptions = token
      ? {
          auth: {
            token,
          },
        }
      : {};

    const SERVER_URL = process.env.NODE_ENV === 'production'
      ? window.location.origin
      : 'http://localhost:3001';
    const socketInstance = io(SERVER_URL, connectionOptions);
    setSocket(socketInstance);

    socketInstance.on('connect', () => {
      setIsConnected(true);
      socketInstance.emit('getInitialData');
    });

    socketInstance.on('disconnect', () => {
      setIsConnected(false);
    });

    socketInstance.on('players', (players) => {
      setPlayers(players);
    });

    socketInstance.on('teams', (teams) => {
      setTeams(teams);
    });

    socketInstance.on('series', (series) => {
      setSeries(series);
    });

    socketInstance.on('tournaments', (tournaments) => {
      setTournaments(tournaments);
    });

    socketInstance.on('matches', (matches) => {
      setMatches(matches);
    });

    return () => {
      socketInstance.off('connect');
      socketInstance.off('disconnect');
      socketInstance.off('players');
      socketInstance.off('teams');
      socketInstance.off('series');
      socketInstance.off('tournaments');
      socketInstance.off('matches');
      socketInstance.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [token]);

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  const pageContent = (
    <div className="app-layout">
      <aside className="sidenav">
        <h2>Valikko</h2>
        {visibleNavItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
          >
            <span className="nav-icon">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </aside>
      <main className="main-content">
        <Routes>
          <Route path="/" element={<Navigate replace to="/matches" />} />
          <Route path="/login" element={isLoggedIn ? <Navigate replace to="/matches" /> : <LoginPage setToken={setToken} />} />
          <Route path="/register" element={isLoggedIn ? <Navigate replace to="/matches" /> : <RegisterPage />} />
          <Route path="/tournaments" element={<Tournaments tournaments={tournaments} teams={teams} matches={matches} socket={socket} user={user} />} />
          <Route path="/tournaments/:slug" element={<Tournaments tournaments={tournaments} teams={teams} matches={matches} socket={socket} user={user} />} />
          <Route path="/matches" element={<MatchPage matches={matches} series={series} tournaments={tournaments} teams={teams} socket={socket} players={players} user={user} />} />
          <Route path="/series" element={isLoggedIn ? <Series series={series} teams={teams} socket={socket} user={user} /> : <Navigate replace to="/matches" />} />
          <Route path="/teams" element={isLoggedIn ? <Teams teams={teams} players={players} socket={socket} user={user} /> : <Navigate replace to="/matches" />} />
          <Route path="/players" element={isLoggedIn ? <Players players={players} teams={teams} socket={socket} user={user} /> : <Navigate replace to="/matches" />} />
          <Route path="/users" element={isLoggedIn ? <Users socket={socket} user={user} /> : <Navigate replace to="/matches" />} />
          <Route path="*" element={<Navigate replace to="/matches" />} />
        </Routes>
      </main>
    </div>
  );

  return (
    <div className="App">
      <header className="App-header">
        <div className="top-nav">
          <div className="brand">
            TBS Hockey
            <span className={`mobile-status-dot ${isConnected ? 'online' : 'offline'}`} aria-label={isConnected ? 'Yhdistetty' : 'Ei yhteyttä'} />
          </div>
          <div className="header-right">
            <div className={`status-badge ${isConnected ? 'online' : 'offline'}`}>
              <span className={`status-dot ${isConnected ? 'online' : 'offline'}`} />
              {isConnected ? 'Yhdistetty' : 'Ei yhteyttä'}
            </div>
            {user && <div className="user-info">{user.username}</div>}
            {token ? (
              <button className="logout-button" onClick={handleLogout}>
                Kirjaudu ulos
              </button>
            ) : (
              <Link to="/login" className="login-button">Kirjaudu</Link>
            )}
          </div>
        </div>
      </header>
      {pageContent}
    </div>
  );
}

export default App;
